import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Package, User, Trash2, Plus, Search, Edit2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_asset-tracker-117/artifacts/sfo88lq2_%E6%B5%8E%E5%8D%97%E6%B2%83%E5%BE%B7LOGO-JPG%E7%89%88.jpg";

const UserManagementPage = ({ user }) => {
  const navigate = useNavigate();
  const [groupedUsers, setGroupedUsers] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [expandedRoles, setExpandedRoles] = useState({});
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    username: "",
    password: "",
    full_name: "",
    email: "",
    phone: "",
    department: "",
    role: "user"
  });
  const [editFormData, setEditFormData] = useState({
    full_name: "",
    email: "",
    phone: "",
    department: "",
    role: "",
    password: ""
  });

  // Redirect if not admin
  useEffect(() => {
    if (user && user.role !== "admin") {
      toast.error("只有管理员可以管理用户");
      navigate("/");
    }
  }, [user, navigate]);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/users/grouped`, {
        params: { search: debouncedSearchTerm || undefined }
      });
      setGroupedUsers(response.data.grouped_users || {});
    } catch (error) {
      toast.error("加载用户列表失败");
    } finally {
      setLoading(false);
    }
  }, [debouncedSearchTerm]);

  useEffect(() => {
    if (user?.role === "admin") {
      fetchUsers();
    }
  }, [user, fetchUsers]);

  const toggleExpanded = (role) => {
    setExpandedRoles(prev => ({
      ...prev,
      [role]: !prev[role]
    }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCreateUser = async () => {
    if (!formData.username.trim() || !formData.password.trim() || !formData.full_name.trim()) {
      toast.error("请填写用户名、密码和姓名");
      return;
    }

    try {
      await axios.post(`${API}/admin/users`, formData);
      toast.success("用户创建成功！");
      setShowCreateDialog(false);
      setFormData({
        username: "",
        password: "",
        full_name: "",
        email: "",
        phone: "",
        department: "",
        role: "user"
      });
      fetchUsers();
    } catch (error) {
      if (error.response?.data?.detail === "Username already exists") {
        toast.error("用户名已存在，请选择其他用户名");
      } else if (error.response?.data?.detail === "Email already exists") {
        toast.error("邮箱已存在，请使用其他邮箱");
      } else {
        toast.error(error.response?.data?.detail || "创建用户失败");
      }
    }
  };

  const handleDeleteUser = async (userId, username) => {
    const confirmMessage = `确认删除用户 "${username}"？\n\n此操作将停用该用户账号，用户将无法登录。`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/users/${userId}`);
      toast.success(`用户 ${username} 已成功停用`);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "删除用户失败");
    }
  };

  const handleEditUser = (userToEdit) => {
    setEditingUser(userToEdit);
    setEditFormData({
      full_name: userToEdit.full_name || "",
      email: userToEdit.email || "",
      phone: userToEdit.phone || "",
      department: userToEdit.department || "",
      role: userToEdit.role || "",
      password: "" // Always start with empty password
    });
    setShowEditDialog(true);
  };

  const handleUpdateUser = async () => {
    if (!editingUser || !editFormData.full_name.trim()) {
      toast.error("请填写必要信息");
      return;
    }

    try {
      // Prepare update data - only include fields that have been changed or are not empty
      const updateData = {};
      
      if (editFormData.full_name.trim() !== editingUser.full_name) {
        updateData.full_name = editFormData.full_name.trim();
      }
      
      if (editFormData.email.trim() !== (editingUser.email || "")) {
        updateData.email = editFormData.email.trim() || null;
      }
      
      if (editFormData.phone.trim() !== (editingUser.phone || "")) {
        updateData.phone = editFormData.phone.trim() || null;
      }
      
      if (editFormData.department.trim() !== (editingUser.department || "")) {
        updateData.department = editFormData.department.trim() || null;
      }
      
      if (editFormData.role !== editingUser.role) {
        updateData.role = editFormData.role;
      }
      
      // Only include password if it's provided
      if (editFormData.password.trim()) {
        updateData.password = editFormData.password.trim();
      }

      await axios.put(`${API}/admin/users/${editingUser.id}`, updateData);
      toast.success("用户信息更新成功");
      
      // Reset form and close dialog
      setShowEditDialog(false);
      setEditingUser(null);
      setEditFormData({
        full_name: "",
        email: "",
        phone: "",
        department: "",
        role: "",
        password: ""
      });
      
      // Refresh users list
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "更新用户失败");
    }
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const getRoleDisplayName = (role) => {
    const roleNames = {
      admin: "管理员",
      manager: "经理", 
      user: "用户"
    };
    return roleNames[role] || role;
  };

  if (user?.role !== "admin") {
    return null;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-slate-900 text-white px-6 py-4">
        <div className="flex items-center space-x-4">
          <Button 
            variant="ghost" 
            onClick={() => navigate("/")}
            className="text-white hover:bg-slate-800"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            返回首页
          </Button>
          <div className="flex items-center space-x-4">
            <img 
              src={LOGO_URL} 
              alt="Company Logo" 
              className="h-8 w-8 object-contain rounded bg-white p-1"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextElementSibling.style.display = 'flex';
              }}
            />
            <div className="w-8 h-8 bg-slate-700 rounded items-center justify-center" style={{display: 'none'}}>
              <Package className="w-4 h-4 text-slate-300" />
            </div>
            <h1 className="text-lg font-semibold">用户管理</h1>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Search and Create User */}
        <div className="mb-6 flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
            <Input
              placeholder="按用户名、姓名、邮箱、部门搜索..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
            {loading && searchTerm && (
              <div className="absolute right-3 top-3">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-slate-400"></div>
              </div>
            )}
          </div>

          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button className="bg-green-600 hover:bg-green-700">
                <Plus className="w-4 h-4 mr-2" />
                创建新用户
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>创建新用户</DialogTitle>
                <DialogDescription>
                  请填写完整信息创建新用户
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="username">用户名 *</Label>
                  <Input
                    id="username"
                    name="username"
                    placeholder="输入唯一用户名"
                    value={formData.username}
                    onChange={handleInputChange}
                  />
                </div>
                <div>
                  <Label htmlFor="password">密码 *</Label>
                  <Input
                    id="password"
                    name="password"
                    type="password"
                    placeholder="输入密码"
                    value={formData.password}
                    onChange={handleInputChange}
                  />
                </div>
                <div>
                  <Label htmlFor="full_name">姓名 *</Label>
                  <Input
                    id="full_name"
                    name="full_name"
                    placeholder="输入完整姓名"
                    value={formData.full_name}
                    onChange={handleInputChange}
                  />
                </div>
                <div>
                  <Label htmlFor="role">角色 *</Label>
                  <select
                    id="role"
                    name="role"
                    value={formData.role}
                    onChange={handleInputChange}
                    className="w-full h-10 px-3 py-2 text-sm border border-slate-200 rounded-md bg-white"
                  >
                    <option value="user">用户</option>
                    <option value="manager">经理</option>
                    <option value="admin">管理员</option>
                  </select>
                </div>
                <div>
                  <Label htmlFor="email">邮箱</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="输入邮箱地址"
                    value={formData.email}
                    onChange={handleInputChange}
                  />
                </div>
                <div>
                  <Label htmlFor="phone">电话</Label>
                  <Input
                    id="phone"
                    name="phone"
                    placeholder="输入电话号码"
                    value={formData.phone}
                    onChange={handleInputChange}
                  />
                </div>
                <div>
                  <Label htmlFor="department">部门</Label>
                  <Input
                    id="department"
                    name="department"
                    placeholder="输入部门名称"
                    value={formData.department}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="flex space-x-4 pt-4">
                  <Button 
                    onClick={handleCreateUser}
                    className="flex-1 bg-green-600 hover:bg-green-700"
                  >
                    创建用户
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => setShowCreateDialog(false)}
                    className="flex-1"
                  >
                    取消
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* Edit User Dialog */}
          <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>编辑用户信息</DialogTitle>
                <DialogDescription>
                  修改用户信息 (用户名不可修改)
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="edit_username">用户名 (不可修改)</Label>
                  <Input
                    id="edit_username"
                    value={editingUser?.username || ""}
                    disabled
                    className="bg-gray-100 text-gray-500"
                  />
                </div>
                <div>
                  <Label htmlFor="edit_full_name">姓名 *</Label>
                  <Input
                    id="edit_full_name"
                    name="full_name"
                    placeholder="输入完整姓名"
                    value={editFormData.full_name}
                    onChange={handleEditInputChange}
                  />
                </div>
                <div>
                  <Label htmlFor="edit_email">邮箱</Label>
                  <Input
                    id="edit_email"
                    name="email"
                    type="email"
                    placeholder="输入邮箱地址"
                    value={editFormData.email}
                    onChange={handleEditInputChange}
                  />
                </div>
                <div>
                  <Label htmlFor="edit_phone">电话</Label>
                  <Input
                    id="edit_phone"
                    name="phone"
                    placeholder="输入电话号码"
                    value={editFormData.phone}
                    onChange={handleEditInputChange}
                  />
                </div>
                <div>
                  <Label htmlFor="edit_department">部门</Label>
                  <Input
                    id="edit_department"
                    name="department"
                    placeholder="输入部门"
                    value={editFormData.department}
                    onChange={handleEditInputChange}
                  />
                </div>
                <div>
                  <Label htmlFor="edit_role">角色 *</Label>
                  <select
                    id="edit_role"
                    name="role"
                    value={editFormData.role}
                    onChange={handleEditInputChange}
                    className="w-full h-10 px-3 py-2 text-sm border border-slate-200 rounded-md bg-white"
                  >
                    <option value="user">用户</option>
                    <option value="manager">经理</option>
                    <option value="admin">管理员</option>
                  </select>
                </div>
                <div>
                  <Label htmlFor="edit_password">新密码 (可选)</Label>
                  <Input
                    id="edit_password"
                    name="password"
                    type="password"
                    placeholder="留空表示不修改密码"
                    value={editFormData.password}
                    onChange={handleEditInputChange}
                  />
                </div>
                <div className="flex space-x-2 pt-4">
                  <Button 
                    onClick={handleUpdateUser} 
                    className="flex-1"
                  >
                    更新用户
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setShowEditDialog(false);
                      setEditingUser(null);
                    }}
                    className="flex-1"
                  >
                    取消
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {searchTerm && (
          <p className="text-xs text-slate-500 mb-4">
            搜索 "{searchTerm}" {loading ? "中..." : `找到 ${Object.values(groupedUsers).reduce((total, users) => total + users.length, 0)} 个结果`}
          </p>
        )}

        {/* Grouped Users List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-800 mx-auto"></div>
            <p className="mt-4 text-slate-600">加载用户列表...</p>
          </div>
        ) : (
          <div className="space-y-6">
            {Object.entries(groupedUsers).map(([role, users]) => (
              <Card key={role} className="shadow-lg">
                <CardHeader 
                  className="cursor-pointer hover:bg-slate-50" 
                  onClick={() => toggleExpanded(role)}
                >
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">{getRoleDisplayName(role)} ({users.length})</CardTitle>
                    <Badge variant="outline">{expandedRoles[role] ? "收起" : "展开"}</Badge>
                  </div>
                </CardHeader>
                
                {expandedRoles[role] && (
                  <CardContent>
                    {/* Table Headers */}
                    <div className="border-b border-slate-200 pb-2 mb-4">
                      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                        <div className="font-semibold text-slate-700 text-sm">用户名</div>
                        <div className="font-semibold text-slate-700 text-sm">姓名</div>
                        <div className="font-semibold text-slate-700 text-sm">邮箱</div>
                        <div className="font-semibold text-slate-700 text-sm">部门</div>
                        <div className="font-semibold text-slate-700 text-sm">操作</div>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      {users.map((userData) => (
                        <div 
                          key={userData.id} 
                          className="cursor-pointer p-3 border rounded-lg hover:shadow-md transition-shadow bg-white border-slate-200"
                        >
                          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-center">
                            {/* Username */}
                            <div>
                              <div className="font-mono text-slate-700">{userData.username}</div>
                            </div>
                            
                            {/* Full Name */}
                            <div>
                              <div className="font-medium text-slate-900">{userData.full_name}</div>
                            </div>
                            
                            {/* Email */}
                            <div>
                              <div className="text-slate-700">{userData.email || "未设置"}</div>
                            </div>
                            
                            {/* Department */}
                            <div>
                              <div className="text-slate-700">{userData.department || "未设置"}</div>
                            </div>
                            
                            {/* Actions */}
                            <div className="flex space-x-2">
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditUser(userData);
                                }}
                              >
                                <Edit2 className="w-4 h-4 mr-1" />
                                编辑
                              </Button>
                              {userData.id !== user.id && userData.role !== "admin" && (
                                <Button 
                                  variant="destructive" 
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteUser(userData.id, userData.username);
                                  }}
                                >
                                  <Trash2 className="w-4 h-4 mr-1" />
                                  停用
                                </Button>
                              )}
                            </div>
                          </div>
                          
                          {/* Additional Info on Mobile */}
                          <div className="mt-2 text-xs text-slate-500 grid grid-cols-2 gap-2 md:hidden">
                            {userData.phone && <div>电话: {userData.phone}</div>}
                            <div>创建: {new Date(userData.created_at).toLocaleDateString('zh-CN')}</div>
                            {userData.last_login && (
                              <div>最后登录: {new Date(userData.last_login).toLocaleDateString('zh-CN')}</div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                )}
              </Card>
            ))}
          </div>
        )}

        {!loading && Object.keys(groupedUsers).length === 0 && (
          <div className="text-center py-12">
            <User className="w-12 h-12 text-slate-400 mx-auto mb-4" />
            <p className="text-slate-600">未找到用户</p>
            {searchTerm && (
              <p className="text-slate-500 text-sm mt-2">
                尝试调整搜索条件
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default UserManagementPage;